import { formatDateLong, type ContentItemDto } from '@nova/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/Button.js';
import { useAreaColor } from '../../lib/area-color.js';
import { cn } from '../../lib/cn.js';

const WEEKDAYS = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'];
const MONTHS = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];

/**
 * Month calendar keyed on publish date. Items without a publish date are listed
 * underneath rather than hidden — an unplanned item is exactly what a planner
 * needs to see.
 */
export function CalendarView({
  items,
  month,
  onMonthChange,
  onOpen,
}: {
  items: ContentItemDto[];
  month: Date;
  onMonthChange: (month: Date) => void;
  onOpen: (item: ContentItemDto) => void;
}) {
  const areaColor = useAreaColor();
  const year = month.getFullYear();
  const monthIndex = month.getMonth();

  const firstOfMonth = new Date(year, monthIndex, 1);
  // Monday-first grid, as Norwegian calendars are.
  const leading = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(year, monthIndex, 1 - leading);

  const days = Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    return date;
  });

  const byDay = new Map<string, ContentItemDto[]>();
  const unplanned: ContentItemDto[] = [];
  for (const item of items) {
    if (!item.publishAt) {
      unplanned.push(item);
      continue;
    }
    const key = dayKey(new Date(item.publishAt));
    byDay.set(key, [...(byDay.get(key) ?? []), item]);
  }

  const today = dayKey(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-ink text-base">
          {MONTHS[monthIndex]} {year}
        </h2>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            aria-label="Forrige måned"
            onClick={() => onMonthChange(new Date(year, monthIndex - 1, 1))}
            icon={<ChevronLeft size={14} aria-hidden />}
          />
          <Button size="sm" onClick={() => onMonthChange(new Date())}>
            I dag
          </Button>
          <Button
            size="sm"
            aria-label="Neste måned"
            onClick={() => onMonthChange(new Date(year, monthIndex + 1, 1))}
            icon={<ChevronRight size={14} aria-hidden />}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-7 gap-px">
            {WEEKDAYS.map((day) => (
              <div key={day} className="label-micro px-2 py-1.5">
                {day}
              </div>
            ))}
          </div>

          <div className="rounded-panel border-hairline bg-hairline grid grid-cols-7 gap-px overflow-hidden border">
            {days.map((date) => {
              const key = dayKey(date);
              const inMonth = date.getMonth() === monthIndex;
              const dayItems = byDay.get(key) ?? [];

              return (
                <div
                  key={key}
                  className={cn(
                    'bg-panel/50 min-h-[110px] p-1.5',
                    !inMonth && 'bg-base/40 opacity-50',
                    key === today && 'bg-accent/[0.06]',
                  )}
                >
                  <div className="mb-1 flex items-center justify-between px-0.5">
                    <span
                      className={cn(
                        'num-tabular text-[11px]',
                        key === today ? 'text-accent font-semibold' : 'text-ink-soft',
                      )}
                    >
                      {date.getDate()}
                    </span>
                  </div>

                  <div className="space-y-1">
                    {dayItems.slice(0, 3).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onOpen(item)}
                        title={item.title}
                        className={cn(
                          'bg-base/60 text-ink hover:bg-raised block w-full truncate rounded border-l-2 px-1.5 py-1 text-left text-[11px] leading-tight transition',
                          item.isOverdue && 'text-danger',
                        )}
                        style={{ borderLeftColor: areaColor(item.area?.color) }}
                      >
                        {item.title}
                      </button>
                    ))}
                    {dayItems.length > 3 && (
                      <p className="text-ink-soft px-1.5 text-[10px]">+{dayItems.length - 3} til</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {unplanned.length > 0 && (
        <section className="rounded-panel border-hairline bg-panel/40 border p-4">
          <p className="label-micro mb-2">Uten publiseringsdato ({unplanned.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {unplanned.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item)}
                className="border-hairline bg-base/50 text-ink hover:border-accent/30 max-w-full truncate rounded-lg border px-2.5 py-1.5 text-left text-[11px] transition"
                style={{ borderLeftColor: areaColor(item.area?.color), borderLeftWidth: 2 }}
                title={`${item.title} — ${formatDateLong(item.createdAt)}`}
              >
                {item.title}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
